import { Router } from "@angular/router";
import { Injectable } from "@angular/core";
import { UserService } from "../api/user/user.service";
import { first, map } from "rxjs/operators";
import { AppSettings } from "../helpers/app-settings";
import { LogService } from "../services/log.service";
import { Observable, Subject } from "rxjs";
import { User, UserPreferences, profileURL } from "../api/user/user.model";
import { Account } from "../api/accounts/account.model";
import { DoctorService } from "../api/doctor/doctor.service";
import { DoctorForm } from "../FormModels/doctor-form.model";
import { parseSavedData, scrumbleDataToSave } from "../helpers/scrumble-data";
import { UserValidationLocalService } from "../services/user-validation-local.service";
import { Doctor } from "../api/doctor/doctor.model";
import { NotificationsService } from "../api/notifications/notifications.service";

@Injectable()
export class AuthService {
  private _user: User = new User();
  public user$: Subject<User> = new Subject<User>();

  public panelType = "patient";

  private loadingDeps = true;



  set user(value: User) {
    this._user = value;

    // Si es que setea nuevo valor con preferences null.
    if (this._user.preferences === null) {
      this._user.preferences = new UserPreferences();
    }

    if (value.id !== null) {
      this.saveUserToLocalStorage();
    }

    this.user$.next(value);
  }

  get user(): User {
    return this._user;
  }

  constructor(
    public router: Router,
    public userService: UserService,
    private appSettings: AppSettings,
    private log: LogService,
    private doctorService: DoctorService,
    private valLocalService: UserValidationLocalService,
    private notificationService: NotificationsService
  ) {
    this.autoLogin();
  }

  saveUserToLocalStorage(): void {
    this.appSettings.set("ud", scrumbleDataToSave<User>(this.user));

    if (!this.loadingDeps && this.user.preferences.app_start_at !== undefined) {
      this.userService
        .savePreferences(this.user.preferences)
        .pipe(first())
        .subscribe();
    }
  }

  /**
   * Setear preferences al usuario y guardar en BD
   */
  setUserPreference(
    preference: string,
    value: any,
    type: string = "string"
  ): void {
    if (type === "string") {
      this.user.preferences[preference] = value;
    } else if (type === "array") {
      if (this.user.preferences[preference] !== undefined) {
        this.user.preferences[preference].push(value);
      } else {
        this.user.preferences[preference] = [value];
      }
    }
    this.saveUserToLocalStorage();
  }

  autoLogin(): void {
    // Si es que ya hay algo en el storage se recupera de ahi
    if (this.appSettings.exists("ud") && this.appSettings.get("ud") !== null) {
      this.user = parseSavedData<User>(this.appSettings.get("ud"), new User());
      this.panelType = this.user.preferences.app_start_at || this.panelType;
    }
  }

  init(): void {
    // Si es que tenemos token, se actualizan datos del usuario al cargar la pagina
    if (
      this.user.access_token !== undefined &&
      this.user.access_token.length > 0
    ) {
      this.refreshUserData();

      // Tambien se actualizan los tokens de validaciones
      this.valLocalService.init();
    }

    this.loadingDeps = false;
  }

  isAfterToday(date) {
    return new Date(date).valueOf() > new Date().valueOf();
  }

  refreshUserData(callback = (data) => data): void {
    this.userService
      .getUserData()
      .pipe(first())
      .subscribe(
        (data) => {
          const tokenOld = this.user.access_token;
          if (data.access_token === undefined) {
            data.access_token = tokenOld;
          }

          this.user = data;

          callback(data);
        },
        (error) => {
          console.log("error", error);
        }
      );
  }

  signupPatient(loginType: string, data: User): Observable<User> {
    return this.userService.registerPatient(loginType, data);
  }

  signupPatientByToken(
    loginType: string,
    data: User,
    token: string
  ): Observable<User> {
    return this.userService.activePatientBytoken(loginType, data, token);
  }

  signupDoctor(loginType: string, data: DoctorForm, signature: string, student: string): Observable<Doctor> {
    return this.doctorService.register(loginType, data, signature, student);
  }

  loginUser(user: string, password: string): Observable<User> {
    return this.userService.login(user, password).pipe(
      map((response) => {
        this.user = response;
        return this.user;
      })
    );
  }

  getUserData(): User {
    return this.user;
  }

  getUserAccountNotIndividual(): Account[] {
    return User.filterIndividualAccounts(this.user.accounts);
  }

  logout(callingApi = true): void {
    if (callingApi) {
      // Antes de borrar, se comprueba si es que hay algun token push que borrar
      if (
        this.appSettings.get("push-token") !== undefined &&
        this.appSettings.get("push-token") !== null
      ) {
        this.notificationService
          .deletePushToken(this.appSettings.get("push-token"))
          .pipe(first())
          .subscribe(() => { });
      }


      this.clearPatientSession();

      this.userService
        .logout()
        .pipe(first())
        .subscribe(() => {
          const pushToken = this.appSettings.get("push-token");
          const userRemember = this.appSettings.get("un_remember", null);

          // Se borran los datos del usuario
          this.appSettings.unsetAll();

          this.appSettings.set("push-token", pushToken);
          if (userRemember !== null) {
            this.appSettings.set("un_remember", userRemember);
          }

          this.user = new User();

          this.panelType = "patient";

          this.router.navigate(["/login"]);
        });
    } else {
      // Se borran los datos del usuario
      this.appSettings.unsetAll();
      this.router.navigate(["/login"]);
    }
  }
  private clearPatientSession(): void {
    sessionStorage.removeItem('current_patient');
    sessionStorage.removeItem('current_patient_id');
    sessionStorage.removeItem('current_patient_name');   // Por si quedó de versiones anteriores
    sessionStorage.removeItem('current_patient_ci');     // Por si quedó de versiones anteriores

    // También limpiamos cualquier presupuesto o consulta en curso para evitar conflictos
    sessionStorage.removeItem('presupuesto_en_curso_id');
    sessionStorage.removeItem('presupuesto_en_curso_patient_id');
  }

  passwordResetRequest(type: string, data: User): Observable<User> {
    return this.userService.resetPasswordRequest(type, data);
  }
  sendTokenVerify(type: string): Observable<User> {
    return this.userService.sendTokenVerify(type);
  }
  sendValidadTokenVerify(type: string, code: string): Observable<User> {
    return this.userService.sendValidadTokenVerify(type, code);
  }

  acceptedInvitationMedical(code: string): Observable<User> {
    return this.userService.acceptedInvitationMedical(code);
  }

  uploadFile(data: any): Observable<profileURL> {
    return this.userService.uploadFile(data);
  }
  isAuthenticated(): boolean {
    return (
      this.user.access_token !== undefined && this.user.access_token !== null
    );
  }
}
